import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import * as XLSX from 'xlsx'
import { FileSpreadsheet, Link, ArrowRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'

interface ExcelUploaderProps {
    onNavigateHome: () => void
}

interface ParsedExchange {
    id: string
    beneficiary_id: string | null
    worker_id: string | null
    colleague_ids: string[] | null
    created_by: string
    info__date: string
    info__duree: number | null
    info__format_echange: string | null
    info__contenu_echange: string | null
    info__contenu_echange_real: string | null
    info__presence: string | null
    info__entree_dans_appartement: string | null
    info__note: string | null
    info__submit_ts: string | null
    updated_at: string | null
    cvc__corps: string | null
    cvc__vetements: string | null
    cvc__comportement: string | null
    hestia__voisinage: string | null
    hestia__hygiene: string | null
    hestia__bailleur: string | null
    hestia_2__securite: string | null
    hestia_2__sante: string | null
    hestia_2__consommation: string | null
    postprocess__n_travailleurs: number | null
    postprocess__is_cvc: boolean | null
    postprocess__is_hestia: boolean | null
    postprocess__is_csv_hestia_filled: boolean | null
    postprocess__cvc__corps: number | null
    postprocess__cvc__vetements: number | null
    postprocess__cvc__comportement: number | null
    postprocess__hestia__voisinage: number | null
    postprocess__hestia__hygiene: number | null
    postprocess__hestia__bailleur: number | null
    postprocess__hestia_2__securite: number | null
    postprocess__hestia_2__sante: number | null
    postprocess__hestia_2__consommation: number | null
}

interface ParsedStat {
    id: string
    beneficiary_id: string | null
    worker_id: string | null
    colleague_ids: string[] | null
    created_by: string
    stat_date: string
    duree: number | null
    format_echange: string | null
    contenu_echange: string | null
    contenu_echange_real: string | null
    presence: string | null
    entree_dans_appartement: string | null
    note: string | null
    submit_ts: string | null
    updated_at: string | null
    cvc_corps: string | null
    cvc_vetements: string | null
    cvc_comportement: string | null
    hestia_voisinage: string | null
    hestia_hygiene: string | null
    hestia_bailleur: string | null
    hestia2_securite: string | null
    hestia2_sante: string | null
    hestia2_consommation: string | null
    postprocess_n_travailleurs: number | null
    postprocess_is_cvc: boolean | null
    postprocess_is_hestia: boolean | null
    postprocess_is_csv_hestia_filled: boolean | null
    postprocess_cvc_corps: number | null
    postprocess_cvc_vetements: number | null
    postprocess_cvc_comportement: number | null
    postprocess_hestia_voisinage: number | null
    postprocess_hestia_hygiene: number | null
    postprocess_hestia_bailleur: number | null
    postprocess_hestia2_securite: number | null
    postprocess_hestia2_sante: number | null
    postprocess_hestia2_consommation: number | null
    comment: string | null
    distribution_kit_rdr: string | null
}

type ParsedRow = ParsedExchange | ParsedStat

interface Profile { id: string; userId: string | null; email: string; workerName: string | null }

interface LookupDictionaries {
    workers: Map<string, string>
    beneficiaries: Map<string, string>
}

interface PreviewResult {
    totalSheetRows: number
    uniqueRows: number        // after dedup within sheet
    newRows: number           // UUIDs not yet in DB
    updateRows: number        // UUIDs already in DB (will upsert)
    invalidRows: number       // couldn't parse / missing required fields
    duplicatesInSheet: number // removed within-sheet duplicates
    processedData: ParsedRow[]
}

type Step = 'input' | 'preview' | 'importing' | 'done'

// ── helpers ────────────────────────────────────────────────────────────────

const convertDateFormat = (dateStr: string): string | null => {
    if (!dateStr) return null
    // ISO: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10)
    // Numeric serial (Excel/Sheets stores dates as numbers)
    if (/^\d+$/.test(dateStr)) {
        const d = XLSX.SSF.parse_date_code(parseInt(dateStr))
        if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`
    }
    const parts = dateStr.split('/')
    if (parts.length !== 3) return null
    const [a, b, c] = parts
    if (!a || !b || !c) return null
    // YYYY/MM/DD
    if (c.length <= 2 && a.length === 4) return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`
    // If a > 12, it must be DD/MM/YYYY (French locale)
    if (parseInt(a) > 12) return `${c}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`
    // If b > 12, it must be MM/DD/YYYY (US locale)
    if (parseInt(b) > 12) return `${c}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`
    // Ambiguous (both ≤ 12): assume MM/DD/YYYY (Excel format)
    return `${c}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`
}

const convertSubmitTs = (submitTs: string): string | null => {
    if (!submitTs) return null
    try {
        const [datePart, timePart] = submitTs.split(', ')
        if (!datePart || !timePart) return null
        // Handle both YYYY/MM/DD and MM/DD/YYYY
        const parts = datePart.split('/')
        if (parts.length !== 3) return null
        const [a, b, c] = parts
        const isoDate = c.length === 4
            ? `${c}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`  // MM/DD/YYYY → YYYY-MM-DD
            : `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`  // YYYY/MM/DD → YYYY-MM-DD
        const d = new Date(`${isoDate}T${timePart}`)
        return isNaN(d.getTime()) ? null : d.toISOString()
    } catch { return null }
}

const convertBoolean = (value: string | null): boolean | null => {
    if (!value) return null
    if (value.toLowerCase() === 'oui') return true
    if (value.toLowerCase() === 'non') return false
    return null
}

const convertNumber = (value: string | null): number | null => {
    if (!value || value === '') return null
    const num = parseInt(value, 10)
    return isNaN(num) ? null : num
}

const parseSheetRows = (xlsxData: any[]): any[] => {
    if (xlsxData.length === 0) return []
    const headers = xlsxData[0]
    return xlsxData.slice(1).map(values => {
        const obj: any = {}
        headers.forEach((header: string, i: number) => {
            const v = values[i]?.toString().trim() || ''
            obj[header.trim()] = v === '' ? null : v
        })
        return obj
    })
}

const parseGoogleSheetsUrl = (url: string): { sheetId: string; gid: string } | null => {
    const idMatch = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/)
    if (!idMatch) return null
    const gidMatch = url.match(/[?#&]gid=(\d+)/)
    return { sheetId: idMatch[1], gid: gidMatch ? gidMatch[1] : '0' }
}

// ── component ──────────────────────────────────────────────────────────────

export const ExcelUploader = ({ onNavigateHome }: ExcelUploaderProps) => {
    const { user } = useAuth()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const selectedTargetTable = 'stats'

    const [profiles, setProfiles] = useState<Profile[]>([])
    const [selectedCreatedBy, setSelectedCreatedBy] = useState<string>('')

    const [sourceType, setSourceType] = useState<'file' | 'sheets'>('file')
    const [sheetsUrl, setSheetsUrl] = useState('')
    const [xlsxData, setXlsxData] = useState<any[]>([])

    const [step, setStep] = useState<Step>('input')
    const [preview, setPreview] = useState<PreviewResult | null>(null)
    const [status, setStatus] = useState('')
    const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [fetchingSheets, setFetchingSheets] = useState(false)

    // ── load profiles on mount ────────────────────────────────────────────
    useEffect(() => {
        supabase.from('profiles').select('id,user_id,email,worker_id,workers(name)').is('deleted_dt', null)
            .then(({ data }) => {
                const list: Profile[] = (data || []).map((p: any) => ({
                    id: p.id,
                    userId: p.user_id ?? null,
                    email: p.email,
                    workerName: p.workers?.name || null,
                }))
                setProfiles(list)
                const current = list.find(p => p.userId === user?.id)
                if (current) setSelectedCreatedBy(current.userId!)
                else if (list.length > 0) setSelectedCreatedBy(list[0].userId || list[0].id)
            })
    }, [])

    // ── data loading ──────────────────────────────────────────────────────

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const wb = XLSX.read(ev.target?.result as ArrayBuffer, { type: 'array' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[]
            setXlsxData(data)
            setStatus(`Fichier chargé : ${data.length - 1} lignes`)
        }
        reader.readAsArrayBuffer(file)
    }

    const handleFetchSheets = async () => {
        const parsed = parseGoogleSheetsUrl(sheetsUrl)
        if (!parsed) { setStatus('URL Google Sheets invalide'); return }
        setFetchingSheets(true)
        setStatus('Chargement depuis Google Sheets...')
        try {
            const csvUrl = `https://docs.google.com/spreadsheets/d/${parsed.sheetId}/export?format=csv&gid=${parsed.gid}`
            const res = await fetch(csvUrl)
            if (!res.ok) throw new Error(`HTTP ${res.status} — vérifie que la feuille est partagée "Tout le monde avec le lien"`)
            const csvText = await res.text()
            const wb = XLSX.read(csvText, { type: 'string' })
            const ws = wb.Sheets[wb.SheetNames[0]]
            const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[]
            setXlsxData(data)
            setStatus(`Feuille chargée : ${data.length - 1} lignes`)
        } catch (err: any) {
            setStatus(`Erreur : ${err.message}`)
        } finally {
            setFetchingSheets(false)
        }
    }

    // ── lookup ────────────────────────────────────────────────────────────

    const fetchLookups = async (): Promise<LookupDictionaries> => {
        const [wr, br] = await Promise.all([
            supabase.from('workers').select('id,name'),
            supabase.from('beneficiaries').select('id,name'),
        ])
        const workers = new Map<string, string>()
        const beneficiaries = new Map<string, string>()
        wr.data?.forEach(w => w.name && workers.set(w.name, w.id))
        br.data?.forEach(b => b.name && beneficiaries.set(b.name, b.id))
        return { workers, beneficiaries }
    }

    // ── row processing (unchanged logic) ─────────────────────────────────

    const processRow = (row: any, dicts: LookupDictionaries, userId: string): ParsedRow | null => {
        if (!row.info__uuid) return null
        const beneficiaryId = dicts.beneficiaries.get(row.info__locataire) || null
        const workerId = dicts.workers.get(row.info__travailleur) || null

        let colleagueIds: string[] = []
        if (row.info__collegue) {
            colleagueIds = row.info__collegue.split(',')
                .map((n: string) => n.trim())
                .filter(Boolean)
                .map((n: string) => dicts.workers.get(n))
                .filter(Boolean) as string[]
        }

        if (selectedTargetTable === 'exchanges') {
            return {
                id: row.info__uuid,
                beneficiary_id: beneficiaryId,
                worker_id: workerId,
                colleague_ids: colleagueIds.length > 0 ? colleagueIds : null,
                created_by: userId,
                info__date: convertDateFormat(row.info__date),
                info__duree: convertNumber(row.info__duree),
                info__format_echange: row.info__format_echange,
                info__contenu_echange: row.info__contenu_echange,
                info__contenu_echange_real: row.info__contenu_echange_real,
                info__presence: row.info__presence,
                info__entree_dans_appartement: row.info__entree_dans_appartement,
                info__note: row.info__note,
                info__submit_ts: convertSubmitTs(row.info__submit_ts),
                updated_at: convertSubmitTs(row.info__submit_ts),
                cvc__corps: row.cvc__corps, cvc__vetements: row.cvc__vetements, cvc__comportement: row.cvc__comportement,
                hestia__voisinage: row.hestia__voisinage, hestia__hygiene: row.hestia__hygiene, hestia__bailleur: row.hestia__bailleur,
                hestia_2__securite: row.hestia_2__securite, hestia_2__sante: row.hestia_2__sante, hestia_2__consommation: row.hestia_2__consommation,
                postprocess__n_travailleurs: convertNumber(row.postprocess__n_travailleurs),
                postprocess__is_cvc: convertBoolean(row.postprocess__is_cvc),
                postprocess__is_hestia: convertBoolean(row.postprocess__is_hestia),
                postprocess__is_csv_hestia_filled: convertBoolean(row.postprocess__is_csv_hestia_filled),
                postprocess__cvc__corps: convertNumber(row.postprocess__cvc__corps),
                postprocess__cvc__vetements: convertNumber(row.postprocess__cvc__vetements),
                postprocess__cvc__comportement: convertNumber(row.postprocess__cvc__comportement),
                postprocess__hestia__voisinage: convertNumber(row.postprocess__hestia__voisinage),
                postprocess__hestia__hygiene: convertNumber(row.postprocess__hestia__hygiene),
                postprocess__hestia__bailleur: convertNumber(row.postprocess__hestia__bailleur),
                postprocess__hestia_2__securite: convertNumber(row.postprocess__hestia_2__securite),
                postprocess__hestia_2__sante: convertNumber(row.postprocess__hestia_2__sante),
                postprocess__hestia_2__consommation: convertNumber(row.postprocess__hestia_2__consommation),
            } as ParsedExchange
        } else {
            return {
                id: row.info__uuid,
                beneficiary_id: beneficiaryId,
                worker_id: workerId,
                colleague_ids: colleagueIds.length > 0 ? colleagueIds : null,
                created_by: userId,
                stat_date: convertDateFormat(row.info__date),
                duree: convertNumber(row.info__duree),
                format_echange: row.info__format_echange,
                contenu_echange: row.info__contenu_echange,
                contenu_echange_real: row.info__contenu_echange_real,
                presence: row.info__presence,
                entree_dans_appartement: row.info__entree_dans_appartement,
                note: row.info__note,
                submit_ts: convertSubmitTs(row.info__submit_ts),
                updated_at: convertSubmitTs(row.info__submit_ts),
                cvc_corps: row.cvc__corps, cvc_vetements: row.cvc__vetements, cvc_comportement: row.cvc__comportement,
                hestia_voisinage: row.hestia__voisinage, hestia_hygiene: row.hestia__hygiene, hestia_bailleur: row.hestia__bailleur,
                hestia2_securite: row.hestia_2__securite, hestia2_sante: row.hestia_2__sante, hestia2_consommation: row.hestia_2__consommation,
                postprocess_n_travailleurs: convertNumber(row.postprocess__n_travailleurs),
                postprocess_is_cvc: convertBoolean(row.postprocess__is_cvc),
                postprocess_is_hestia: convertBoolean(row.postprocess__is_hestia),
                postprocess_is_csv_hestia_filled: convertBoolean(row.postprocess__is_csv_hestia_filled),
                postprocess_cvc_corps: convertNumber(row.postprocess__cvc__corps),
                postprocess_cvc_vetements: convertNumber(row.postprocess__cvc__vetements),
                postprocess_cvc_comportement: convertNumber(row.postprocess__cvc__comportement),
                postprocess_hestia_voisinage: convertNumber(row.postprocess__hestia__voisinage),
                postprocess_hestia_hygiene: convertNumber(row.postprocess__hestia__hygiene),
                postprocess_hestia_bailleur: convertNumber(row.postprocess__hestia__bailleur),
                postprocess_hestia2_securite: convertNumber(row.postprocess__hestia_2__securite),
                postprocess_hestia2_sante: convertNumber(row.postprocess__hestia_2__sante),
                postprocess_hestia2_consommation: convertNumber(row.postprocess__hestia_2__consommation),
                comment: null,
                distribution_kit_rdr: null,
            } as ParsedStat
        }
    }

    // ── preview ───────────────────────────────────────────────────────────

    const handleBuildPreview = async () => {
        if (!xlsxData.length || !selectedCreatedBy) return
        setLoadingPreview(true)
        setStatus('Analyse en cours...')
        try {
            const dicts = await fetchLookups()
            const rows = parseSheetRows(xlsxData)
            const totalSheetRows = rows.length

            // Process all rows
            const processed: ParsedRow[] = []
            let invalidRows = 0
            for (const row of rows) {
                const p = processRow(row, dicts, selectedCreatedBy)
                if (p) processed.push(p)
                else invalidRows++
            }

            // Deduplicate within sheet: sort by submit_ts desc, keep most recent per UUID
            const getTs = (r: ParsedRow) =>
                ('submit_ts' in r ? r.submit_ts : r.info__submit_ts) ?? ''
            const seenIds = new Set<string>()
            const deduped = [...processed]
                .sort((a, b) => getTs(b).localeCompare(getTs(a)))
                .filter(r => {
                    if (seenIds.has(r.id)) return false
                    seenIds.add(r.id)
                    return true
                })
            const duplicatesInSheet = processed.length - deduped.length

            // Check which IDs already exist in DB (batched to avoid URL length limit)
            const ids = deduped.map(r => r.id).filter(Boolean)
            const existingIds = new Set<string>()
            const idBatchSize = 200
            for (let i = 0; i < ids.length; i += idBatchSize) {
                const { data: existing } = await supabase
                    .from(selectedTargetTable)
                    .select('id')
                    .in('id', ids.slice(i, i + idBatchSize))
                existing?.forEach(r => existingIds.add(r.id))
            }

            setPreview({
                totalSheetRows,
                uniqueRows: deduped.length,
                newRows: deduped.filter(r => !existingIds.has(r.id)).length,
                updateRows: deduped.filter(r => existingIds.has(r.id)).length,
                invalidRows,
                duplicatesInSheet,
                processedData: deduped,
            })
            setStep('preview')
            setStatus('')
        } catch (err: any) {
            setStatus(`Erreur : ${err.message}`)
        } finally {
            setLoadingPreview(false)
        }
    }

    // ── import ────────────────────────────────────────────────────────────

    const handleImport = async () => {
        if (!preview) return
        setStep('importing')
        setStatus('Import en cours...')
        setProgress({ current: 0, total: preview.processedData.length })

        const batchSize = 50
        let uploaded = 0
        const errors: string[] = []
        for (let i = 0; i < preview.processedData.length; i += batchSize) {
            const batch = preview.processedData.slice(i, i + batchSize)
            const { error } = await supabase
                .from(selectedTargetTable)
                .upsert(batch, { onConflict: 'id' })
            if (error) {
                console.error(`Batch ${i} error:`, error)
                errors.push(`Batch ${i / batchSize + 1} : ${error.message} (code ${error.code})`)
                // Stop after first error to show it immediately
                if (errors.length === 1) break
            } else {
                uploaded += batch.length
                setProgress({ current: uploaded, total: preview.processedData.length })
            }
        }
        if (errors.length > 0) {
            setStatus(`❌ ${uploaded} importés, ${errors.length} erreur(s) :\n${errors.join('\n')}`)
        } else {
            setStatus(`✅ ${uploaded} enregistrements importés dans "${selectedTargetTable}"`)
        }
        setStep('done')
        setProgress(null)
    }

    const reset = () => {
        setXlsxData([])
        setPreview(null)
        setStep('input')
        setStatus('')
        setProgress(null)
        setSheetsUrl('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    // ── render ────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Import XLSX" icon={<FileSpreadsheet size={20} />} onBack={onNavigateHome} />

            <main className="max-w-3xl mx-auto py-8 px-4 space-y-6">

                {/* ── STEP: INPUT ── */}
                {(step === 'input' || step === 'preview' || step === 'done') && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Créé par <span className="text-gray-400 font-normal">(user ID utilisé pour created_by)</span>
                        </label>
                        <select
                            className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={selectedCreatedBy}
                            onChange={e => setSelectedCreatedBy(e.target.value)}
                        >
                            {profiles.map(p => (
                                <option key={p.id} value={p.userId || p.id}>
                                    {p.workerName ? `${p.workerName} — ${p.email}` : p.email}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {(step === 'input' || step === 'done') && (
                    <div className="bg-white rounded-lg shadow p-6 space-y-5">

                        {/* Source tabs */}
                        <div>
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setSourceType('file')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${sourceType === 'file' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                >
                                    <FileSpreadsheet size={14} /> Fichier XLSX
                                </button>
                                <button
                                    onClick={() => setSourceType('sheets')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${sourceType === 'sheets' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                >
                                    <Link size={14} /> Google Sheets
                                </button>
                            </div>

                            {sourceType === 'file' && (
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            )}

                            {sourceType === 'sheets' && (
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="https://docs.google.com/spreadsheets/d/..."
                                            value={sheetsUrl}
                                            onChange={e => setSheetsUrl(e.target.value)}
                                            className="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        <Button onClick={handleFetchSheets} disabled={!sheetsUrl || fetchingSheets}
                                            className="shrink-0 flex items-center gap-1.5">
                                            {fetchingSheets ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                                            Charger
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-400">
                                        Copie l'URL depuis l'onglet voulu (elle contient <code>gid=…</code>).
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Status */}
                        {status && (
                            <p className={`text-sm ${status.startsWith('Erreur') ? 'text-red-600' : 'text-gray-600'}`}>
                                {status}
                            </p>
                        )}

                        {/* Analyse button */}
                        {xlsxData.length > 0 && (
                            <div className="flex justify-between items-center pt-1">
                                <span className="text-sm text-gray-500">{xlsxData.length - 1} lignes chargées</span>
                                <Button onClick={handleBuildPreview} disabled={loadingPreview}
                                    className="flex items-center gap-2">
                                    {loadingPreview
                                        ? <><Loader2 size={14} className="animate-spin" /> Analyse...</>
                                        : <>Analyser <ArrowRight size={14} /></>}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP: PREVIEW ── */}
                {step === 'preview' && preview && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="font-semibold text-gray-900 mb-4">Résumé avant import → <span className="text-blue-600">{selectedTargetTable}</span></h2>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                                <Stat label="Lignes dans la feuille" value={preview.totalSheetRows} color="gray" />
                                <Stat label="Doublons dans la feuille" value={preview.duplicatesInSheet} color="gray"
                                    sub="version la plus récente conservée" />
                                <Stat label="Invalides" value={preview.invalidRows} color={preview.invalidRows > 0 ? 'red' : 'gray'}
                                    sub="UUID/bénéficiaire manquant" />
                                <Stat label="Nouvelles entrées" value={preview.newRows} color="green"
                                    sub="seront insérées" />
                                <Stat label="Déjà en DB" value={preview.updateRows} color="blue"
                                    sub="seront mises à jour (upsert)" />
                                <Stat label="Total à importer" value={preview.uniqueRows} color="gray" bold />
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setStep('input')}>Retour</Button>
                                <Button onClick={handleImport} disabled={preview.uniqueRows === 0}
                                    className="flex items-center gap-2">
                                    Confirmer l'import <ArrowRight size={14} />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP: IMPORTING ── */}
                {step === 'importing' && (
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <Loader2 size={18} className="animate-spin text-blue-500" />
                            <span className="text-gray-700 font-medium">Import en cours...</span>
                        </div>
                        {progress && (
                            <div>
                                <div className="flex justify-between text-sm text-gray-500 mb-1">
                                    <span>{progress.current} / {progress.total}</span>
                                    <span>{Math.round((progress.current / progress.total) * 100)}%</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div className="bg-blue-500 h-2 rounded-full transition-all"
                                        style={{ width: `${(progress.current / progress.total) * 100}%` }} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP: DONE ── */}
                {step === 'done' && (
                    <div className="bg-white rounded-lg shadow p-6 space-y-4">
                        <div className="flex items-start gap-3">
                            {status.startsWith('✅')
                                ? <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
                                : <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />}
                            <pre className="text-sm text-gray-800 font-medium whitespace-pre-wrap">{status}</pre>
                        </div>
                        <Button variant="outline" onClick={reset}>Nouvel import</Button>
                    </div>
                )}
            </main>
        </div>
    )
}

// ── small stat card ────────────────────────────────────────────────────────

const Stat = ({ label, value, color, sub, bold }: {
    label: string; value: number; color: 'gray' | 'green' | 'blue' | 'red'; sub?: string; bold?: boolean
}) => {
    const colors = {
        gray: 'text-gray-800', green: 'text-green-600', blue: 'text-blue-600', red: 'text-red-600'
    }
    return (
        <div className="bg-gray-50 rounded-lg p-3">
            <p className={`text-2xl font-bold ${colors[color]} ${bold ? 'text-3xl' : ''}`}>{value}</p>
            <p className="text-xs font-medium text-gray-600 mt-0.5">{label}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    )
}

export const CsvUploader = ExcelUploader
export default ExcelUploader
